import { isPaidEvent } from "@circle-react/components/EventsV3/Modal/utils";
import { ShowPageLink } from "@circle-react/components/PostsV3/PostViewListItem/ShowPageLink";
import { useCurrentPostContext } from "@circle-react/contexts";
import { EventIconTooltip } from "../../ListViewItem/EventIconTooltip";
import { EventMenu } from "../../ListViewItem/EventMenu";
import { EVENT_TYPES, RSVP_STATUS, STATUS } from "../../helpers/constants";
import { RsvpActionButton } from "../EventDetailPage/RsvpActionButton";
import { EventTypeIcon, RsvpWarningMessages, ThumbnailImage } from "../shared";
import { InvitedByTab } from "../shared/InvitedByTab";
import { canRespondToInvitation, isEventFinished } from "../shared/helpers";
import { EventLocation } from "./EventLocation";
import { ListViewEventTimestamp } from "./ListViewEventTimestamp";
import { ListViewTicketInfo } from "./ListViewTicketInfo";

interface Props {
  onDestroy: () => void;
  onRsvp: () => void;
}

export const MainContent = ({ onDestroy, onRsvp }: Props) => {
  const { record: event } = useCurrentPostContext();

  const {
    name,
    status,
    event_type: eventType,
    event_setting_attributes: eventData,
    recurring_setting_attributes: eventRecurringSettings,
    paywall_attributes: ticketData,
    rsvp_status: rsvpStatus,
    community_member: communityMember,
  } = event;
  const { starts_at: startsAt, location_type: locationType } = eventData || {};

  const isRecurring = eventType === EVENT_TYPES.RECURRING;
  const isPublished = status === STATUS.PUBLISHED;
  const shouldDisplayRsvpAction =
    isPublished && !isEventFinished(event) && canRespondToInvitation(event);
  const isAttendeeInvited = rsvpStatus == RSVP_STATUS.INVITED;

  return (
    <div className="border-primary border-b p-6 last:border-none">
      {isAttendeeInvited && <InvitedByTab name={communityMember.name} />}

      <div className="border-primary flex w-full flex-col last:border-none md:h-auto md:flex-row">
        <ThumbnailImage event={event} />
        <div
          className="flex w-full justify-between"
          data-testid="event-main-content"
        >
          <div className="flex flex-col justify-start">
            <ShowPageLink
              post={event}
              className="!text-dark mb-2 !line-clamp-2 max-w-[307px] break-words text-base font-semibold"
            >
              {name}
            </ShowPageLink>
            <div className="flex flex-col items-start gap-2">
              <div className="flex items-center gap-x-2 leading-3">
                <div className="hidden md:block">
                  <EventIconTooltip
                    startsAt={startsAt}
                    frequency={eventRecurringSettings?.frequency}
                    disabled={!isRecurring}
                  >
                    <EventTypeIcon type={eventType} />
                  </EventIconTooltip>
                </div>
                <ListViewEventTimestamp event={event} isThumbnailView />
              </div>
              <EventLocation type={locationType} />
              {isPaidEvent(event) && (
                <ListViewTicketInfo ticketData={ticketData} />
              )}
              <RsvpWarningMessages event={event} />
            </div>
          </div>
          <div className="ml-2 flex shrink-0 items-start gap-2">
            <div className="hidden md:block">
              {shouldDisplayRsvpAction && (
                <RsvpActionButton
                  event={event}
                  onChange={onRsvp}
                  variant="primary"
                />
              )}
            </div>
            <EventMenu event={event} onDestroy={onDestroy} />
          </div>
        </div>
        {shouldDisplayRsvpAction && (
          <div className="mt-6 block md:hidden">
            <RsvpActionButton
              event={event}
              onChange={onRsvp}
              variant="primary"
            />
          </div>
        )}
      </div>
    </div>
  );
};
